import ApiInstance from "./ApiInstance";

export interface Psychologist {
  _id: string;
  displayName: string;
  photoUrl?: string;
  bio?: string;
  languages: string[];
  specializations: string[];
  regionsServed: string[];
  rateCents: number;
  coachingRateCents?: number;
  therapyRateCents?: number;
  offersCoaching?: boolean;
  gender?: string;
  profession?: string;
  eligibleFundCodes?: string[];
}

export interface ProfileInput {
  displayName: string;
  bio?: string;
  languages: string[];
  regionsServed: string[];
  specializations: string[];
  gender?: string;
  rateCents: number;
  coachingRateCents?: number;
  therapyRateCents?: number;
  offersCoaching?: boolean;
  // BTW-kwalificatie coaching (art. 44): door de psycholoog zelf verklaard.
  coachingTherapeuticPurpose?: boolean;
  // Annulatiebeleid (no-show): flexible (default) of strict_24h (50% bij <24u).
  lateCancellationPolicy?: string;
  // Vetting / credentials.
  profession?: string;
  visaNumber?: string;
  erkenningNumber?: string;
  rizivNumber?: string;
  psychologenCommissieNumber?: string;
  professionalAssociation?: string;
  associationMemberNumber?: string;
  psychotherapyLegalBasis?: string;
  // DAC7 / BTW intake (fee invoicing + yearly FOD Financiën report).
  businessForm?: string; // "natural_person" | "company"
  vatStatus?: string; // "none" | "exempt_only" | "franchise" | "mixed"
  vatNumber?: string;
  tin?: string; // rijksregisternummer (natural persons)
  kboNumber?: string; // KBO (companies)
  birthDate?: string; // yyyy-mm-dd (natural persons, DAC7)
  // Hoofdadres (DAC7-verkopersrapport, wettelijk verplicht).
  addressStreet?: string;
  addressPostalCode?: string;
  addressCity?: string;
}

export interface ProviderDoc {
  id: string;
  kind: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

class PsychologistApi {
  async get(id: string): Promise<Psychologist> {
    const { data } = await ApiInstance.getApi().get(`/psychologists/${id}`);
    return data;
  }
  async getMine(): Promise<(Psychologist & { verifiedState?: string; onboardingCompletedAt?: string }) | null> {
    const { data } = await ApiInstance.getApi().get("/psychologists/me");
    return data.profile;
  }
  async completeOnboarding(): Promise<{ ok: boolean; onboardingCompletedAt: string }> {
    // Het klik-akkoord met de samenwerkingsovereenkomst is de ondertekening
    // (versioned gelogd in de consent-ledger) — de wizard toont de checkbox.
    const { data } = await ApiInstance.getApi().post("/psychologists/me/onboarding/complete", { acceptTerms: true });
    return data;
  }
  async getTerms(): Promise<{ version: string; markdown: string }> {
    const { data } = await ApiInstance.getApi().get("/psychologists/terms");
    return data;
  }
  async updateMine(body: ProfileInput): Promise<{ id: string; verifiedState: string }> {
    const { data } = await ApiInstance.getApi().post("/psychologists/me", body);
    return data;
  }
  async payoutsStatus(): Promise<{ connected: boolean; payoutsEnabled: boolean; detailsSubmitted?: boolean; stripeConfigured: boolean }> {
    const { data } = await ApiInstance.getApi().get("/psychologists/me/payouts/status");
    return data;
  }
  async payoutsOnboard(): Promise<{ url?: string; stub?: boolean }> {
    const { data } = await ApiInstance.getApi().post("/psychologists/me/payouts/onboard", {});
    return data;
  }
  async handoff(token: string): Promise<{ ok: boolean; displayName: string }> {
    const { data } = await ApiInstance.getApi().post("/psychologists/handoff", { token });
    return data;
  }
  // Publieke lijst (verified + accepting), optioneel gefilterd op thema/regio —
  // gebruikt door de SEO-directorypagina's (/psychologen/...).
  async list(topic?: string, region?: string): Promise<Psychologist[]> {
    const params = new URLSearchParams();
    if (topic) params.set("topic", topic);
    if (region) params.set("region", region);
    const { data } = await ApiInstance.getApi().get(`/psychologists${params.toString() ? `?${params}` : ""}`);
    return data.psychologists;
  }
  async listDocuments(): Promise<ProviderDoc[]> {
    const { data } = await ApiInstance.getApi().get("/psychologists/me/documents");
    return data.documents;
  }
  async uploadDocument(body: { kind: string; filename: string; contentType: string; dataBase64: string }): Promise<ProviderDoc> {
    const { data } = await ApiInstance.getApi().post("/psychologists/me/documents", body);
    return data;
  }
  async deleteDocument(id: string): Promise<void> {
    await ApiInstance.getApi().delete(`/psychologists/me/documents/${id}`);
  }
}

const psychologistApi = new PsychologistApi();
export default psychologistApi;
